import {DialogEventEmitter} from "./event-emitter";
import {
    DialogEvent,
    DialogEventType,
    DialogPhase,
    DialogRequest,
    DialogResult,
    OrchestratorContext,
} from "./types";

/**
 * Owns the per-instance state machine and turns phase transitions into events.
 *
 * Valid transitions:
 *   requested → queued → waiting-dependency? → opening → open → closing → closed
 *   (any) → canceled
 *   open → replaced
 *
 * The manager validates transitions defensively (logs and ignores illegal ones)
 * so a race can never throw inside the orchestration loop.
 */
const ALLOWED: Record<DialogPhase, DialogPhase[]> = {
    requested: ["queued", "opening", "cancelled"],
    queued: ["waiting-dependency", "opening", "cancelled"],
    "waiting-dependency": ["queued", "opening", "cancelled"],
    opening: ["open", "cancelled", "closed"],
    open: ["closing", "replaced", "cancelled"],
    closing: ["closed"],
    closed: [],
    cancelled: [],
    replaced: ["closed"],
};

const PHASE_EVENT: Partial<Record<DialogPhase, DialogEventType>> = {
    queued: "queued",
    open: "opened",
    closed: "closed",
    cancelled: "cancelled",
    replaced: "replaced",
};

export class LifecycleManager {
    constructor(private readonly emitter: DialogEventEmitter) {}

    canTransition(from: DialogPhase, to: DialogPhase): boolean {
        return ALLOWED[from]?.includes(to) ?? false;
    }

    /**
     * Apply a transition to `request` (mutates `phase`) and emit the matching
     * lifecycle event. Returns false if the transition was illegal.
     */
    transition(
        request: DialogRequest,
        to: DialogPhase,
        ctx: OrchestratorContext,
        result?: DialogResult,
    ): boolean {
        if (request.phase === to) return true;
        if (!this.canTransition(request.phase, to)) {

            console.warn(
                `[dialog-orchestrator] illegal transition ${request.id} ${request.phase} → ${to}`,
            );
            return false;
        }
        request.phase = to;
        const eventType = PHASE_EVENT[to];
        if (eventType) this.emit(eventType, ctx, request, result);
        return true;
    }

    /** Emit non-phase events (focus, blocked, flow locks). */
    emit(
        type: DialogEventType,
        ctx: OrchestratorContext,
        request?: Pick<DialogRequest, "id" | "instanceId">,
        result?: DialogResult,
        flow?: string,
    ): void {
        const event: DialogEvent = {
            type,
            id: request?.id,
            instanceId: request?.instanceId,
            flow,
            ctx,
            result,
            at: Date.now(),
        };
        this.emitter.emit(event);
    }
}
